import { Routes, Route, Navigate } from 'react-router-dom'

import { ThemeProvider } from '@/components/providers/theme-provider'
import { ToastProvider } from '@/components/providers/toast-provider'
import { Helmet } from 'react-helmet-async'
import { Suspense, lazy, useEffect } from 'react'
import { DashboardRedirect } from '@/components/auth/role-redirect'
import { AdminRoute, StaffRoute } from '@/components/auth/ProtectedRoute'
import { TaskRedirect } from '@/components/auth/TaskRedirect'

// Layouts
import DashboardLayout from '@/layouts/DashboardLayout'
import StaffLayout from '@/layouts/StaffLayout'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, AppDispatch } from './store'
import { fetchAllMasterData } from '@/store/slices/masterDataSlice'

// Loading component
const Loading = () => (
  <div className="flex h-screen items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
  </div>
)

// Landing page
const HomePage = lazy(() => import('@/app/page'))

// Auth Page
const LoginPage = lazy(() => import('@/app/login/page'))

// Main Dashboard Pages
const AIChatPage = lazy(() => import('@/app/(dashboard)/ai-chat/page'))
const AIGenerationPage = lazy(() => import('@/app/(dashboard)/ai-generation/page'))
const AIPersonasPage = lazy(() => import('@/app/(dashboard)/ai-personas/page'))
const AssignmentsPage = lazy(() => import('@/app/(dashboard)/assignments/page'))
const Sprints = lazy(() => import('@/app/(dashboard)/admin/sprints/page'))
const JiraSetupPage = lazy(() => import('@/app/(dashboard)/admin/jira-setup/page'))
const BoardPage = lazy(() => import('@/app/(dashboard)/admin/board/page'))
const IssueTypesPage = lazy(() => import('@/app/(dashboard)/admin/issue-types/page'))
const WorkflowsPage = lazy(() => import('@/app/(dashboard)/admin/workflows/page'))
const EmployeesPage = lazy(() => import('@/app/(dashboard)/employees/page'))
const AttendancePage = lazy(() => import('@/app/(dashboard)/attendance/page'))
const UsersPage = lazy(() => import('@/app/(dashboard)/users/page'))
const TasksPage = lazy(() => import('@/app/(dashboard)/tasks/page'))
const TimesheetPage = lazy(() => import('@/app/(dashboard)/timesheet/page'))
const DepartmentsPage = lazy(() => import('@/app/(dashboard)/departments/page'))
const DeploymentPage = lazy(() => import('@/app/(dashboard)/deployment/page'))
const NotificationsPage = lazy(() => import('@/app/(dashboard)/notifications/page'))
const ProjectsPage = lazy(() => import('@/app/(dashboard)/projects/page'))
const ProjectDetailsPage = lazy(() => import('@/app/(dashboard)/projects/[id]/page'))
const ProjectNewPage = lazy(() => import('@/app/(dashboard)/projects/new/page'))
const ProjectStatusPage = lazy(() => import('@/app/(dashboard)/projects/status/page'))
const ReviewQueuePage = lazy(() => import('@/app/(dashboard)/review-queue/page'))
const CompliancePage = lazy(() => import('@/app/(dashboard)/compliance/page'))
const ContextManagementPage = lazy(() => import('@/app/(dashboard)/context-management/page'))
const HealthcareProtocolsPage = lazy(() => import('@/app/(dashboard)/healthcare-protocols/page'))
const MyAiConversationsPage = lazy(() => import('@/app/(dashboard)/my-ai-conversations/page'))
const PromptLibraryPage = lazy(() => import('@/app/(dashboard)/prompt-library/page'))
const TeamMembersPage = lazy(() => import('@/app/(dashboard)/team-members/page'))

// Analytics Pages
const AnalyticsPage = lazy(() => import('@/app/(dashboard)/analytics/page'))
const AnalyticsOverviewPage = lazy(() => import('@/app/(dashboard)/analytics/overview/page'))
const AnalyticsInsightsPage = lazy(() => import('@/app/(dashboard)/analytics/insights/page'))
const AnalyticsDepartmentPage = lazy(() => import('@/app/(dashboard)/analytics/department/page'))
const AnalyticsPersonalPage = lazy(() => import('@/app/(dashboard)/analytics/personal/page'))
const AnalyticsPlatformPage = lazy(() => import('@/app/(dashboard)/analytics/platform/page'))
const AnalyticsProjectsPage = lazy(() => import('@/app/(dashboard)/analytics/projects/page'))
const AnalyticsPerformancePage = lazy(() => import('@/app/(dashboard)/analytics/performance/page'))
const AnalyticsTeamPage = lazy(() => import('@/app/(dashboard)/analytics/team/page'))
const AnalyticsUsagePage = lazy(() => import('@/app/(dashboard)/analytics/usage/page'))
const AnalyticsReportsPage = lazy(() => import('@/app/(dashboard)/analytics/reports/page'))
const AnalyticsReportsSalesPage = lazy(() => import('@/app/(dashboard)/analytics/reports/sales/page'))
const AnalyticsReportsUsersPage = lazy(() => import('@/app/(dashboard)/analytics/reports/users/page'))

// Admin Pages
const AdminConfigPage = lazy(() => import('@/app/(dashboard)/admin/config/page'))
const AdminConversationsPage = lazy(() => import('@/app/(dashboard)/admin/conversations/page'))
const AdminCostTrackingPage = lazy(() => import('@/app/(dashboard)/admin/cost-tracking/page'))
const AdminDailyTasksPage = lazy(() => import('@/app/(dashboard)/admin/daily-tasks/page'))
const AdminDashboardPage = lazy(() => import('@/app/(dashboard)/admin/dashboard/page'))
const AdminEmployeeRatesPage = lazy(() => import('@/app/(dashboard)/admin/employee-rates/page'))
const AdminEmployeeSkillsPage = lazy(() => import('@/app/(dashboard)/admin/employee-skills/page'))
const AdminEmployeeTimesheetsPage = lazy(() => import('@/app/(dashboard)/admin/employees/[id]/timesheets/page'))
const AdminHealthPage = lazy(() => import('@/app/(dashboard)/admin/health/page'))
const AdminImportExportPage = lazy(() => import('@/app/(dashboard)/admin/import-export/page'))
const AdminInfrastructurePage = lazy(() => import('@/app/(dashboard)/admin/infrastructure/page'))
const AdminLicensesPage = lazy(() => import('@/app/(dashboard)/admin/licenses/page'))
const AdminMonitoringPage = lazy(() => import('@/app/(dashboard)/admin/monitoring/page'))
const AdminMonthlyOverviewPage = lazy(() => import('@/app/(dashboard)/admin/monthly-overview/page'))
const AdminOrganizationsPage = lazy(() => import('@/app/(dashboard)/admin/organizations/page'))
const AdminProjectDashboardPage = lazy(() => import('@/app/(dashboard)/admin/project-dashboard/page'))
const AdminProjectSpendingPage = lazy(() => import('@/app/(dashboard)/admin/project-spending/page'))
const AdminProjectTasksPage = lazy(() => import('@/app/(dashboard)/admin/project-tasks/page'))
const AdminTeamProgressPage = lazy(() => import('@/app/(dashboard)/admin/team-progress/page'))
const AdminTimesheetsPage = lazy(() => import('@/app/(dashboard)/admin/timesheets/page'))
const AdminUsersPage = lazy(() => import('@/app/(dashboard)/admin/users/page'))
const AdminWeeklySummaryPage = lazy(() => import('@/app/(dashboard)/admin/weekly-summary/page'))

// Billing Pages
const BillingPage = lazy(() => import('@/app/(dashboard)/billing/page'))
const BillingAllocationsPage = lazy(() => import('@/app/(dashboard)/billing/allocations/page'))
const BillingAnalyticsPage = lazy(() => import('@/app/(dashboard)/billing/analytics/page'))
const BillingBudgetsPage = lazy(() => import('@/app/(dashboard)/billing/budgets/page'))
const BillingCostsPage = lazy(() => import('@/app/(dashboard)/billing/costs/page'))
const BillingExportPage = lazy(() => import('@/app/(dashboard)/billing/export/page'))
const BillingModelsPage = lazy(() => import('@/app/(dashboard)/billing/models/page'))
const BillingSubscriptionPage = lazy(() => import('@/app/(dashboard)/billing/subscription/page'))
const BillingUsagePage = lazy(() => import('@/app/(dashboard)/billing/usage/page'))

// Development Pages
const DevelopmentApiBuilderPage = lazy(() => import('@/app/(dashboard)/development/api-builder/page'))
const DevelopmentCodeEditorPage = lazy(() => import('@/app/(dashboard)/development/code-editor/page'))
const DevelopmentCommitsPage = lazy(() => import('@/app/(dashboard)/development/commits/page'))
const DevelopmentDiagnosticsPage = lazy(() => import('@/app/(dashboard)/development/diagnostics/page'))
const DevelopmentScaffoldingPage = lazy(() => import('@/app/(dashboard)/development/scaffolding/page'))
const DevelopmentTestingPage = lazy(() => import('@/app/(dashboard)/development/testing/page'))
const DevelopmentVersionControlPage = lazy(() => import('@/app/(dashboard)/development/version-control/page'))

// Generation Pages
const GenerationPage = lazy(() => import('@/app/(dashboard)/generation/page'))
const GenerationCodePage = lazy(() => import('@/app/(dashboard)/generation/code/page'))
const GenerationDocsPage = lazy(() => import('@/app/(dashboard)/generation/docs/page'))
const GenerationNewPage = lazy(() => import('@/app/(dashboard)/generation/new/page'))
const GenerationTestsPage = lazy(() => import('@/app/(dashboard)/generation/tests/page'))

// Settings Pages
const SettingsPage = lazy(() => import('@/app/(dashboard)/settings/page'))
const SettingsApiPage = lazy(() => import('@/app/(dashboard)/settings/api/page'))
const SettingsApiKeysPage = lazy(() => import('@/app/(dashboard)/settings/api-keys/page'))
const SettingsBillingPage = lazy(() => import('@/app/(dashboard)/settings/billing/page'))
const SettingsGeneralPage = lazy(() => import('@/app/(dashboard)/settings/general/page'))
const SettingsNotificationsPage = lazy(() => import('@/app/(dashboard)/settings/notifications/page'))
const SettingsPreferencesPage = lazy(() => import('@/app/(dashboard)/settings/preferences/page'))
const SettingsProfilePage = lazy(() => import('@/app/(dashboard)/settings/profile/page'))
const SettingsProjectPage = lazy(() => import('@/app/(dashboard)/settings/project/page'))
const SettingsTeamPage = lazy(() => import('@/app/(dashboard)/settings/team/page'))

// Resources Pages
const ResourcesDocumentationPage = lazy(() => import('@/app/(dashboard)/resources/documentation/page'))
const ResourcesTemplatesPage = lazy(() => import('@/app/(dashboard)/resources/templates/page'))

// Integration Center Pages
const IntegrationCenterPage = lazy(() => import('@/app/(dashboard)/integration-center/page'))
const ApiManagementPage = lazy(() => import('@/app/(dashboard)/integration-center/api-management/page'))
const BrowserAutomationPage = lazy(() => import('@/app/(dashboard)/integration-center/browser-automation/page'))
const HealthcarePage = lazy(() => import('@/app/(dashboard)/integration-center/healthcare/page'))
const ThirdPartyPage = lazy(() => import('@/app/(dashboard)/integration-center/third-party/page'))

// Staff Pages
const StaffPage = lazy(() => import('@/app/staff/page'))
const StaffDashboard = lazy(() => import('@/app/staff/dashboard/page'))
const StaffCalendar = lazy(() => import('@/app/staff/calendar/page'))
const StaffProjects = lazy(() => import('@/app/staff/projects/page'))
const StaffTasks = lazy(() => import('@/app/staff/tasks/page'))
const StaffTimesheets = lazy(() => import('@/app/staff/timesheets/page'))
const StaffDocuments = lazy(() => import('@/app/staff/documents/page'))
const StaffProfile = lazy(() => import('@/app/staff/profile/page'))
const StaffProfileUpdate = lazy(() => import('@/app/staff/profile/update/page'))
const StaffAvatarAnalysis = lazy(() => import('@/app/staff/avatar-analysis/page'))
const ChatSystemPage = lazy(() => import('@/app/staff/chat-system/page'))

// Test Page
const TestSessionPage = lazy(() => import('@/app/test-session/page'))

// Site config for metadata
const siteConfig = {
  name: 'InstaDev',
  description: 'Comprehensive Development Solutions'
}

function App() {
  const dispatch = useDispatch<AppDispatch>()
  const isSuperAdmin = useSelector((state: RootState) => state.auth.isSuperAdmin)
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated)
  const userType = useSelector((state: RootState) => state.auth.userType)

  // Fetch master data only for admin users (not staff/employees)
  useEffect(() => {
    if (isAuthenticated && userType?.toLowerCase() === 'admin') {
      dispatch(fetchAllMasterData(false))
    }
  }, [isAuthenticated, userType, dispatch])

  return (

    <ThemeProvider>
      <Helmet>
        <title>{siteConfig.name}</title>
        <meta name="description" content={siteConfig.description} />
      </Helmet>

      <Suspense fallback={<Loading />}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/test-session" element={<TestSessionPage />} />

          {/* Universal Task Route - Redirects to admin or staff based on role */}
          <Route path="/task/:taskId" element={<TaskRedirect />} />

          {/* Auth Route - Single SSO Login */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/" element={<LoginPage />} />

          {/* Dashboard Routes with Layout - Admin Only */}
          <Route
            path="/dashboard"
            element={
              <AdminRoute>
                <DashboardLayout />
              </AdminRoute>
            }
          >
            <Route index element={<DashboardRedirect />} />

            {/* Main Dashboard Pages */}
            <Route path="ai-chat" element={<AIChatPage />} />
            <Route path="community-chat" element={<ChatSystemPage />} />
            <Route path="ai-generation" element={<AIGenerationPage />} />
            <Route path="ai-personas" element={<AIPersonasPage />} />
            <Route path="assignments" element={<AssignmentsPage />} />
            {/* <Route path="chat-system" element={<ChatSystemPage />} /> */}
            {
              isSuperAdmin &&
              <>
                <Route path="employees" element={<EmployeesPage />} />
                <Route path="admin/weekly-summary" element={<AdminWeeklySummaryPage />} />
                <Route path="admin/monthly-overview" element={<AdminMonthlyOverviewPage />} />
                <Route path="admin/project-dashboard" element={<AdminProjectDashboardPage />} />
                <Route path="users" element={<UsersPage />} />
              </>
            }
            <Route path="attendance" element={<AttendancePage />} />

            <Route path="tasks" element={<TasksPage />} />
            <Route path="timesheet" element={<TimesheetPage />} />
            <Route path="departments" element={<DepartmentsPage />} />
            <Route path="deployment" element={<DeploymentPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="compliance" element={<CompliancePage />} />
            <Route path="context-management" element={<ContextManagementPage />} />
            <Route path="healthcare-protocols" element={<HealthcareProtocolsPage />} />
            <Route path="my-ai-conversations" element={<MyAiConversationsPage />} />
            <Route path="prompt-library" element={<PromptLibraryPage />} />
            <Route path="review-queue" element={<ReviewQueuePage />} />
            <Route path="team-members" element={<TeamMembersPage />} />

            {/* Projects Routes */}
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="projects/new" element={<ProjectNewPage />} />
            <Route path="projects/status" element={<ProjectStatusPage />} />
            <Route path="projects/:id" element={<ProjectDetailsPage />} />

            {/* Analytics Routes */}
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="analytics/overview" element={<AnalyticsOverviewPage />} />
            <Route path="analytics/insights" element={<AnalyticsInsightsPage />} />
            <Route path="analytics/department" element={<AnalyticsDepartmentPage />} />
            <Route path="analytics/personal" element={<AnalyticsPersonalPage />} />
            <Route path="analytics/platform" element={<AnalyticsPlatformPage />} />
            <Route path="analytics/projects" element={<AnalyticsProjectsPage />} />
            <Route path="analytics/performance" element={<AnalyticsPerformancePage />} />
            <Route path="analytics/team" element={<AnalyticsTeamPage />} />
            <Route path="analytics/usage" element={<AnalyticsUsagePage />} />
            <Route path="analytics/reports" element={<AnalyticsReportsPage />} />
            <Route path="analytics/reports/sales" element={<AnalyticsReportsSalesPage />} />
            <Route path="analytics/reports/users" element={<AnalyticsReportsUsersPage />} />

            {/* Admin Routes */}
            <Route path="admin/config" element={<AdminConfigPage />} />
            <Route path="admin/conversations" element={<AdminConversationsPage />} />
            <Route path="admin/cost-tracking" element={<AdminCostTrackingPage />} />
            <Route path="admin/daily-tasks" element={<AdminDailyTasksPage />} />
            <Route path="admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="admin/employee-rates" element={<AdminEmployeeRatesPage />} />
            <Route path="admin/employee-skills" element={<AdminEmployeeSkillsPage />} />
            <Route path="admin/employees/:id/timesheets" element={<AdminEmployeeTimesheetsPage />} />
            <Route path="admin/health" element={<AdminHealthPage />} />
            <Route path="admin/import-export" element={<AdminImportExportPage />} />
            <Route path="admin/infrastructure" element={<AdminInfrastructurePage />} />
            <Route path="admin/licenses" element={<AdminLicensesPage />} />
            <Route path="admin/monitoring" element={<AdminMonitoringPage />} />

            {/* JIRA Features */}
            <Route path="admin/jira-setup" element={<JiraSetupPage />} />
            <Route path="admin/board" element={<BoardPage />} />
            <Route path="admin/issue-types" element={<IssueTypesPage />} />
            <Route path="admin/workflows" element={<WorkflowsPage />} />
            <Route path="admin/sprints" element={<Sprints />} />

            <Route path="admin/organizations" element={<AdminOrganizationsPage />} />

            <Route path="admin/project-spending" element={<AdminProjectSpendingPage />} />
            <Route path="admin/project-tasks" element={<AdminProjectTasksPage />} />
            <Route path="admin/team-progress" element={<AdminTeamProgressPage />} />
            <Route path="admin/timesheets" element={<AdminTimesheetsPage />} />
            <Route path="admin/users" element={<AdminUsersPage />} />


            {/* Billing Routes */}
            <Route path="billing" element={<BillingPage />} />
            <Route path="billing/allocations" element={<BillingAllocationsPage />} />
            <Route path="billing/analytics" element={<BillingAnalyticsPage />} />
            <Route path="billing/budgets" element={<BillingBudgetsPage />} />
            <Route path="billing/costs" element={<BillingCostsPage />} />
            <Route path="billing/export" element={<BillingExportPage />} />
            <Route path="billing/models" element={<BillingModelsPage />} />
            <Route path="billing/subscription" element={<BillingSubscriptionPage />} />
            <Route path="billing/usage" element={<BillingUsagePage />} />

            {/* Development Routes */}
            <Route path="development/api-builder" element={<DevelopmentApiBuilderPage />} />
            <Route path="development/code-editor" element={<DevelopmentCodeEditorPage />} />
            <Route path="development/commits" element={<DevelopmentCommitsPage />} />
            <Route path="development/diagnostics" element={<DevelopmentDiagnosticsPage />} />
            <Route path="development/scaffolding" element={<DevelopmentScaffoldingPage />} />
            <Route path="development/testing" element={<DevelopmentTestingPage />} />
            <Route path="development/version-control" element={<DevelopmentVersionControlPage />} />

            {/* Generation Routes */}
            <Route path="generation" element={<GenerationPage />} />
            <Route path="generation/code" element={<GenerationCodePage />} />
            <Route path="generation/docs" element={<GenerationDocsPage />} />
            <Route path="generation/new" element={<GenerationNewPage />} />
            <Route path="generation/tests" element={<GenerationTestsPage />} />

            {/* Settings Routes */}
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/api" element={<SettingsApiPage />} />
            <Route path="settings/api-keys" element={<SettingsApiKeysPage />} />
            <Route path="settings/billing" element={<SettingsBillingPage />} />
            <Route path="settings/general" element={<SettingsGeneralPage />} />
            <Route path="settings/notifications" element={<SettingsNotificationsPage />} />
            <Route path="settings/preferences" element={<SettingsPreferencesPage />} />
            <Route path="settings/profile" element={<SettingsProfilePage />} />
            <Route path="settings/project" element={<SettingsProjectPage />} />
            <Route path="settings/team" element={<SettingsTeamPage />} />

            {/* Resources Routes */}
            <Route path="resources/documentation" element={<ResourcesDocumentationPage />} />
            <Route path="resources/templates" element={<ResourcesTemplatesPage />} />

            {/* Integration Center Routes */}
            <Route path="integration-center" element={<IntegrationCenterPage />} />
            <Route path="integration-center/api-management" element={<ApiManagementPage />} />
            <Route path="integration-center/browser-automation" element={<BrowserAutomationPage />} />
            <Route path="integration-center/healthcare" element={<HealthcarePage />} />
            <Route path="integration-center/third-party" element={<ThirdPartyPage />} />
          </Route>

          {/* Staff Routes with Layout - Staff Only */}
          <Route
            path="/staff"
            element={
              <StaffRoute>
                <StaffLayout />
              </StaffRoute>
            }
          >
            <Route index element={<StaffPage />} />
            <Route path="dashboard" element={<StaffDashboard />} />
            <Route path="ai-chat" element={<AIChatPage />} />
            <Route path="chat-system" element={<ChatSystemPage />} />
            <Route path="calendar" element={<StaffCalendar />} />
            <Route path="projects" element={<StaffProjects />} />
            <Route path="tasks" element={<StaffTasks />} />
            <Route path="timesheets" element={<StaffTimesheets />} />
            <Route path="documents" element={<StaffDocuments />} />
            <Route path="profile" element={<StaffProfile />} />
            <Route path="profile/update" element={<StaffProfileUpdate />} />
            <Route path="avatar-analysis" element={<StaffAvatarAnalysis />} />
            <Route path="board" element={<BoardPage />} />
            
            
            <Route path="sprints" element={<Sprints />} />

          </Route>

          {/* Fallback Routes */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      <ToastProvider />
    </ThemeProvider>

  )
}

export default App